// One collection, read from the local store with the unsent writes folded over
// it (#207).
//
// The `onSnapshot` listener keeps its shape; this hook takes what it delivers,
// **writes it to the store**, and answers every read from
// `overlay.readCollection` instead of from the snapshot. The listener decides
// the window, the store mirrors it — there is no query engine here.
//
// ── Three things make it re-read, and only one of them is a snapshot ─────
//
//   1. **mount.** Before any snapshot: a cold offline start has stored rows
//      from a previous session and queued writes from this one, and both are
//      on the screen before the network is consulted about anything.
//   2. **a snapshot.** Stored first, then read back — never rendered directly
//      from `snap.docs`, because a read that skipped the store would skip the
//      overlay with it (#237).
//   3. **the queue moved** (`onQueueChange`). The overlay is derived from the
//      queue on every read, so an enqueue changes the answer with nothing in
//      the store having changed. Without this, a teacher's own save would sit
//      invisible until the next snapshot — which offline is never.
//
// ── Imports no Firebase ──────────────────────────────────────────────────
//
// `subscribe` and `path` are both handed in: the caller owns the query
// (`useCollectionWindow` builds it), and `src/data/readStore.js` owns the
// singleton store. What is left here is the state machine, tested in the `ui`
// project, which cannot reach Firebase at all.
//
// ── A stale read is marked, never blocked (#207 Q5) ──────────────────────
//
// This hook returns `state` untouched and refuses nothing: it does not hold a
// render back for a `stale` window, throw on one, or wait for a snapshot
// before answering. Which screens say something about a stale read is not
// this hook's question.
//
// `loading` is `collectionIsLoading` (#267) and not `state === 'loading'`: a
// studio with nothing stored that has queued a creation this session has
// something to show, and a spinner over it would hide the write the teacher
// just made.

import { useEffect, useState } from 'react';
import { collectionIsLoading, changeTouches } from './overlay';

// ── A read that did not change must not schedule a render (#379) ───────────
//
// `onQueueChange` wakes every mounted provider (see `writes.js`: the overlay
// re-derives the whole queue on every read), so one enqueue → dispatch →
// settle cycle re-reads collections whose answer did not change. #389 skips
// the reads a change cannot touch (below); this skips the renders for what
// still re-reads: mount, a snapshot, and a transition whose extent the queue
// could not enumerate.
//
// `overlay.readCollection` hands back the previous object when the new answer
// equals it, and that alone is not enough: React's `dispatchSetState` bails
// out on `Object.is` only while the fiber has no pending lanes, and under this
// fan-out it almost always has some. So the guard is explicit. It compares
// *identity*, not value — the overlay owns equality, and this file must not
// grow a second opinion about when two reads are the same.
//
// Still dispatched: the first answer of every subscription, for each of the
// three setters, and `setError(null)` whenever there is an error to clear.
// This must never skip a render for a value the tree is not already holding,
// which is why the closure variables are written only where the setter beside
// them is called.
//
// ── Why the first answer is always dispatched ──────────────────────────────
//
// The three variables live in the **effect's** closure; the state they guard
// lives in the **component**, and outlives it. So each starts as *this
// subscription has not dispatched yet*, which is not any value the state
// might be holding. Starting `lastError` at `null` would let a new
// subscription's first clean read compare equal, skip `setError(null)`, and
// leave the previous subscription's failure on screen; starting
// `lastStoreRefusals` at `EMPTY_REFUSALS` would do the same to a list of
// documents the store had refused.
//
// `UNSET` is a value no overlay read, `Error` or refusal list can be, so
// `!== UNSET` is *this subscription has spoken*, and nothing is inherited. The
// cost is at most one extra dispatch per setter per subscription. A
// resubscription happens when the uid, the collection or the query changes;
// `subscribe` is keyed on `buildQuery` in `useCollectionWindow`, and so on its
// `limit`.

/** One shared empty list, so `|| []` cannot be a new identity per snapshot. */
const EMPTY_REFUSALS = Object.freeze([]);

/**
 * *This subscription has not dispatched this value yet* — distinct from every
 * value it could dispatch, so a new subscription cannot inherit the last one's
 * answer. See the note above.
 */
const UNSET = Symbol('nothing dispatched by this subscription');

/** What a caller sees before the first read has come back. */
const NOTHING_YET = {
  docs: [],
  state: null,
  seenAt: null,
  complete: false,
  refusals: [],
  storeRefusals: [],
  error: null,
  loading: true,
};

/**
 * @param uid         the studio. Falsy means signed out: nothing is read.
 * @param collection  a `COLLECTIONS` name.
 * @param subscribe   `(onWindow) => unsubscribe`, where `onWindow` takes
 *                    `[{ id, data }]` **in delivered order**. The provider
 *                    builds the query; this hook never sees Firestore.
 * @param path        `theReadPath()` — `{ store, overlay, onQueueChange }`.
 * @param enabled     the flag. `false` makes this hook inert: no subscription,
 *                    no store write, no read. See `readStoreFlag.js`.
 *
 * Returns `{ docs, state, seenAt, complete, refusals, storeRefusals, error,
 * loading, failed, confirmed }`. `confirmed` (#651): whether the server has
 * ever answered this scope on this device. `docs` is
 * `[{ id, data, pendingWrite, partial }]` — `partial` (#268) says *this is the
 * document and not all of it is here*.
 */
export function useStoredCollection({
  uid, collection, subscribe, path, enabled = true,
}) {
  const [read, setRead] = useState(null);
  // `EMPTY_REFUSALS` rather than a literal, so the first snapshot of a healthy
  // studio matches what is already held and schedules nothing (#379).
  const [storeRefusals, setStoreRefusals] = useState(EMPTY_REFUSALS);
  const [error, setError] = useState(null);
  // The listener's own error (#647), kept apart from `error`: a read of the
  // store that succeeds afterwards — a queue change re-reads — clears
  // `error`, and must not make a listener that has stopped look alive. Only
  // a new subscription clears this.
  const [listenError, setListenError] = useState(null);

  useEffect(() => {
    setListenError(null);
    if (!enabled || !uid || !path) {
      setRead(null);
      setStoreRefusals(EMPTY_REFUSALS);
      setError(null);
      return undefined;
    }

    let live = true;
    // Reads are started by three independent triggers and each is a couple of
    // IndexedDB round trips, so two can be in flight at once and settle in
    // either order. The newest read wins rather than the last to arrive —
    // otherwise a snapshot's read could be overwritten by an older one started
    // by an enqueue a moment earlier, and the screen would go backwards.
    let latest = 0;

    // What this subscription last handed each setter, so an unchanged answer
    // schedules nothing (#379, see the note above this hook). `UNSET`, because
    // these are the effect's and the state is the component's, so only "not
    // yet" is true at this point.
    let lastRead = UNSET;
    let lastError = UNSET;
    let lastStoreRefusals = UNSET;

    const reread = () => {
      const token = (latest += 1);
      // Two-argument `then`, not `.then().catch()`: this rejection handler is
      // for the read's own failure and must not become a catch for a bug in
      // the handler beside it.
      path.overlay.readCollection(uid, collection).then(
        (next) => {
          if (!live || token !== latest) return;
          // Identity, from `overlay.readCollection`'s own cache. Equality is
          // that file's decision; this one only declines to re-dispatch what
          // the tree is already holding (#379).
          if (next !== lastRead) {
            lastRead = next;
            setRead(next);
          }
          // `!== null` catches both an error this subscription reported and
          // `UNSET` — its first clean read, which must clear whatever the
          // *previous* subscription left in `error`. After that it is null and
          // a healthy stream dispatches nothing.
          if (lastError !== null) {
            lastError = null;
            setError(null);
          }
        },
        (err) => {
          if (!live || token !== latest) return;
          // A refusal `docShape` raised never reaches here — the overlay
          // catches those per document and reports them in `refusals`, so one
          // bad field cannot take a roster down. Anything that does reach here
          // is something else, and it is reported rather than swallowed.
          console.error(`[readStore] reading ${collection} failed:`, err);
          lastError = err;
          setError(err);
        },
      );
    };

    // ── A queue change that cannot have moved this collection is not read
    //    at all (#389) ───────────────────────────────────────────────────────
    //
    // The #379 skip above removes the *renders* behind the fan-out; this
    // removes the reads — each an IndexedDB round trip and a wasm crossing —
    // using the paths `writes.js`'s signal carries.
    //
    // `changeTouches` is the overlay's, because the claim is the overlay's —
    // it folds only the ops under this prefix, so a change naming none of them
    // cannot move this read. It answers `true` for a change whose extent the
    // queue could not enumerate, which keeps this from reintroducing #237.
    const prefix = `users/${uid}/${collection}/`;
    const unsubscribeQueue = path.onQueueChange((change) => {
      if (changeTouches(change, prefix)) reread();
    });
    // `meta.fromCache` is the snapshot's (#651): whether this window came from
    // the SDK's cache rather than the server, which the store records so a
    // device that never synced can be told from an empty studio. A subscriber
    // that does not say is taken as the server's.
    const unsubscribeSnapshot = subscribe((docs, meta) => {
      path.store.replaceCollection(uid, collection, docs, {
        fromCache: Boolean(meta && meta.fromCache === true),
      }).then(
        (result) => {
          if (!live) return;
          // Documents the store would not carry, named with the field that
          // stopped them. Kept apart from the read's own `refusals`: one is
          // what could not be *stored*, the other what could not be *folded*.
          //
          // An identity comparison, so the common case never re-renders.
          // `.length ? … : EMPTY_REFUSALS` rather than `|| EMPTY_REFUSALS`:
          // `appStore` hands back its *own* shared empty list, which is
          // truthy, so `||` would let a second empty identity through.
          // Normalising here makes every empty answer this hook sees the same
          // object, including the first, without either file importing the
          // other's constant.
          const refused = result?.refusals?.length ? result.refusals : EMPTY_REFUSALS;
          if (refused !== lastStoreRefusals) {
            lastStoreRefusals = refused;
            setStoreRefusals(refused);
          }
          reread();
        },
        (err) => {
          if (!live) return;
          console.error(`[readStore] storing a ${collection} snapshot failed:`, err);
          lastError = err;
          setError(err);
          // Still re-read. The store is unchanged, so this shows the previous
          // window with the queue folded over it — one snapshot behind, which
          // on this path is better than blank.
          reread();
        },
      );
    }, (err) => {
      // The listener stopped (#647): refused by the rules, or the connection
      // failed before it answered. Firestore does not restart it, so nothing
      // further will arrive on this subscription; what the store holds is
      // still read and shown, and `failed` below says so only where there is
      // nothing to show.
      if (!live) return;
      console.error(`[readStore] listening to ${collection} failed:`, err);
      setListenError(err);
    });

    // Before the network says anything. The whole point of a read store.
    reread();

    return () => {
      live = false;
      unsubscribeQueue();
      if (typeof unsubscribeSnapshot === 'function') unsubscribeSnapshot();
    };
  }, [uid, collection, subscribe, path, enabled]);

  if (!enabled) return { ...NOTHING_YET, loading: false, enabled: false };
  // `failed` (#647): the listener stopped, or the device's own store could not
  // be read at all. Either way no answer is coming on this subscription.
  if (read === null) {
    return {
      ...NOTHING_YET, storeRefusals, error, failed: Boolean(listenError || error), enabled: true,
    };
  }
  return {
    docs: read.docs,
    state: read.state,
    seenAt: read.seenAt,
    complete: read.complete,
    refusals: read.refusals,
    storeRefusals,
    error,
    loading: collectionIsLoading(read),
    failed: Boolean(listenError),
    // The server has answered this scope on this device (#651). `undefined`
    // from a store that cannot say counts as answered — see `appStore`'s
    // `receiptConfirmed`, which makes the same call for an old receipt.
    confirmed: read.confirmed !== false,
    enabled: true,
  };
}
